/* What The Puzzles — in-theme admin dashboard.
 * Login via Supabase Auth, list all store products, flag missing images,
 * upload a custom image (Supabase Storage) and edit puzzle extras.
 * Security = Supabase Auth + RLS. Requires window.supabase. */
(function () {
  "use strict";

  var root = document.getElementById("wtp-admin");
  if (!root) return;

  var BUCKET = "puzzle-images";
  var sb = null; // supabase client

  // ---- helpers --------------------------------------------------------------
  function el(html) {
    var t = document.createElement("template");
    t.innerHTML = html.trim();
    return t.content.firstChild;
  }
  function esc(s) {
    return String(s == null ? "" : s).replace(/[&<>"']/g, function (c) {
      return { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c];
    });
  }
  function money(v) {
    if (v == null || v === "") return "";
    var n = Number(v);
    return isNaN(n) ? "" : "$" + n.toFixed(2);
  }

  // ---- wait for the supabase client ----------------------------------------
  function whenSupabaseReady(cb) {
    if (window.supabase) return cb(window.supabase);
    var done = false;
    function go() {
      if (done || !window.supabase) return;
      done = true;
      cb(window.supabase);
    }
    document.addEventListener("supabase:ready", go);
    var tries = 0;
    var iv = setInterval(function () {
      tries++;
      if (window.supabase || tries > 50) {
        clearInterval(iv);
        if (window.supabase) go();
        else if (!done) {
          root.innerHTML =
            '<div class="wtp-admin__error">Supabase client not loaded. ' +
            "Check snippets/supabase-client.liquid keys.</div>";
        }
      }
    }, 100);
  }

  // ---- auth -----------------------------------------------------------------
  async function isAdmin() {
    var sess = await sb.auth.getSession();
    if (!sess.data.session) return false;
    var uid = sess.data.session.user.id;
    var res = await sb.from("admins").select("id").eq("id", uid).maybeSingle();
    return !res.error && !!res.data;
  }

  function renderLogin(message) {
    root.innerHTML = "";
    var box = el(
      '<div class="wtp-admin__login">' +
        "<h1>Admin Login</h1>" +
        (message ? '<p class="wtp-admin__error">' + esc(message) + "</p>" : "") +
        '<form class="wtp-admin__form">' +
        '<label>Email<input type="email" name="email" required autocomplete="username"></label>' +
        '<label>Password<input type="password" name="password" required autocomplete="current-password"></label>' +
        '<button type="submit">Log in</button>' +
        "</form>" +
        "</div>",
    );
    var form = box.querySelector("form");
    form.addEventListener("submit", async function (e) {
      e.preventDefault();
      var btn = form.querySelector("button");
      btn.disabled = true;
      btn.textContent = "Logging in…";
      var email = form.email.value.trim();
      var password = form.password.value;
      var res = await sb.auth.signInWithPassword({ email: email, password: password });
      if (res.error) {
        btn.disabled = false;
        btn.textContent = "Log in";
        return renderLogin(res.error.message);
      }
      if (!(await isAdmin())) {
        await sb.auth.signOut();
        return renderLogin("This account is not an admin.");
      }
      renderDashboard();
    });
    root.appendChild(box);
  }

  // ---- data -----------------------------------------------------------------
  var productsPromise = null;
  function fetchAllProducts() {
    if (productsPromise) return productsPromise;
    productsPromise = (async function () {
      var all = [];
      // Only DIGITAL puzzles (client request) — fetch the digital-puzzles collection
      // instead of the whole catalogue. Faster, and inherently digital-only.
      for (var page = 1; page <= 5; page++) {
        // M13: retry a Shopify blip (429 / 5xx) instead of showing an empty grid.
        var data = null;
        for (var attempt = 0; attempt < 4; attempt++) {
          var r = null;
          try { r = await fetch("/collections/digital-puzzles/products.json?limit=250&page=" + page, {
            headers: { Accept: "application/json" } }); } catch (e) { r = null; }
          if (r && r.ok) { data = await r.json(); break; }
          if (r && r.status !== 429 && r.status < 500) break;
          await new Promise(function (res) { setTimeout(res, 800 * Math.pow(2, attempt)); });
        }
        if (!data) throw new Error("The store's product list didn't load — please refresh.");
        var list = data.products || [];
        all = all.concat(list);
        if (list.length < 250) break;
      }
      // Safety filter in case the collection ever includes a stray non-digital item.
      return all.filter(function (p) { return p.handle && p.handle.indexOf("digital-") === 0; });
    })().catch(function (e) { productsPromise = null; throw e; });
    return productsPromise;
  }
  // Prefetch immediately — runs in parallel with the Supabase CDN script + login check.
  try { fetchAllProducts().catch(function () {}); } catch (e) {}

  async function fetchPuzzleMap() {
    var map = {};
    var res = await sb
      .from("puzzles")
      .select("shopify_product_id, handle, custom_image_url, difficulty, piece_count, is_active");
    if (!res.error && res.data) {
      res.data.forEach(function (row) {
        if (row.handle) map[row.handle] = row;
      });
    }
    return map;
  }

  // ---- dashboard ------------------------------------------------------------
  async function renderDashboard() {
    root.innerHTML = '<div class="wtp-admin__loading">Loading products…</div>';

    var products, puzzleMap;
    try {
      // Parallel: products (Shopify) and puzzle rows (Supabase) don't depend on each other.
      var both = await Promise.all([fetchAllProducts(), fetchPuzzleMap()]);
      products = both[0];
      puzzleMap = both[1];
    } catch (e) {
      root.innerHTML = '<div class="wtp-admin__error">Failed to load: ' + esc(e.message || e) + "</div>";
      return;
    }

    root.innerHTML = "";
    var header = el(
      '<div class="wtp-admin__header">' +
        "<h1>Puzzle Admin</h1>" +
        '<div class="wtp-admin__tabs" role="tablist">' +
          '<button type="button" class="wtp-admin__tab is-on" data-tab="puzzles">Puzzles</button>' +
          '<button type="button" class="wtp-admin__tab" data-tab="avatars">Avatars ' +
            '<span class="wtp-admin__count" hidden></span></button>' +
          '<button type="button" class="wtp-admin__tab" data-tab="analytics">Analytics</button>' +
          '<button type="button" class="wtp-admin__tab" data-tab="credits">Credits ' +
            '<span class="wtp-admin__count wtp-admin__count--cr" hidden></span></button>' +
        "</div>" +
        '<div class="wtp-admin__meta">' +
        products.length +
        " products • " +
        products.filter(function (p) {
          return !(p.images && p.images.length) && !(puzzleMap[p.handle] && puzzleMap[p.handle].custom_image_url);
        }).length +
        " missing image" +
        ' <button class="wtp-admin__logout" type="button">Log out</button></div>' +
        "</div>",
    );
    header.querySelector(".wtp-admin__logout").addEventListener("click", async function () {
      await sb.auth.signOut();
      renderLogin();
    });
    root.appendChild(header);

    var grid = el('<div class="wtp-admin__grid"></div>');
    products.forEach(function (p) {
      grid.appendChild(renderCard(p, puzzleMap[p.handle]));
    });
    root.appendChild(grid);

    // M12: avatar moderation lives in its own tab.
    var avPanel = el('<div class="wtp-admin__avpanel" hidden></div>');
    root.appendChild(avPanel);
    var anPanel = el('<div class="wtp-admin__anpanel" hidden></div>');   // M14
    root.appendChild(anPanel);
    var crPanel = el('<div class="wtp-admin__crpanel" hidden></div>');   // v78: credit tools
    root.appendChild(crPanel);
    var loaded = false, anLoaded = false, crLoaded = false;
    header.querySelector(".wtp-admin__tabs").addEventListener("click", function (e) {
      var t = e.target.closest("[data-tab]");
      if (!t) return;
      var tab = t.getAttribute("data-tab");
      header.querySelectorAll(".wtp-admin__tab").forEach(function (b) {
        b.classList.toggle("is-on", b.getAttribute("data-tab") === tab);
      });
      grid.hidden = tab !== "puzzles";
      avPanel.hidden = tab !== "avatars";
      anPanel.hidden = tab !== "analytics";
      crPanel.hidden = tab !== "credits";
      if (tab === "avatars" && !loaded) { loaded = true; renderAvatars(avPanel, "all"); }   // v83: open on every picture
      if (tab === "analytics" && !anLoaded) { anLoaded = true; renderAnalytics(anPanel, 30); }
      if (tab === "credits" && !crLoaded) { crLoaded = true; renderCredits(crPanel); }
    });
    refreshAvatarCount();
    refreshFlaggedCount();
  }

  // ---- v78: credit tools -------------------------------------------------------
  // Sep 30 Scope & Plan: "A simple admin page lets you find any player, see their
  // balance and history, and add or remove credits with a note", and duplicate First
  // Purchase Offer orders are "flagged to you for a refund". Every call is an
  // admin_* RPC that checks is_admin() itself.
  var REASON = {
    purchase: "Credit pack bought", physical_purchase: "Earned from a puzzle order", stash_unlock: "Stash Box unlocked",
    completion: "Finished a puzzle (Locked Credits)", consumable: "Used a helper", cosmetic: "Bought a cosmetic",
    share: "Share reward", refund: "Refund — credit pack", refund_earn: "Refund — puzzle order",
    refund_unlock: "Refund — Stash unlock", admin_adjust: "Admin adjustment", test_grant: "Test credits",
    first_offer_blocked: "First Purchase Offer bought again — not credited",
  };
  function crNum(v) { return (Math.round(Number(v || 0) * 100) / 100).toLocaleString(); }
  function words(code) { code = String(code || "").replace(/_/g, " "); return code.charAt(0).toUpperCase() + code.slice(1); }

  async function refreshFlaggedCount() {
    try {
      var r = await sb.rpc("admin_flagged_orders");
      var c = root.querySelector(".wtp-admin__count--cr");
      var k = r && r.data && r.data.ok ? r.data.items.length : 0;
      if (c) { c.textContent = k; c.hidden = !k; }
    } catch (e) { /* the badge is a nicety */ }
  }

  // v81 (user request 2026-10-07): the tab opens on EVERY player (newest activity first),
  // and the search box narrows that list as you type, or with Search. Both come from
  // admin_list_players; an empty search means everyone. Opening a player shows their card
  // ABOVE the list and scrolls to it.
  // v81b (user request 2026-10-07: "add the pagination", then "10 users per list"): 10 players a
  // page, with ‹ Prev · 1 2 3 … · Next › and "Showing 11–20 of 47 players". A new search starts at page 1.
  var CR_PAGE = 10;

  // The page buttons to draw: every page when there are few, otherwise the first, the last,
  // and the current page with its neighbours, with gaps ("…") between.
  function pageList(cur, last) {
    if (last <= 7) { var all = []; for (var i = 1; i <= last; i++) all.push(i); return all; }
    var out = [1], from = Math.max(2, cur - 1), to = Math.min(last - 1, cur + 1);
    if (cur <= 3) { from = 2; to = 4; }
    if (cur >= last - 2) { from = last - 3; to = last - 1; }
    if (from > 2) out.push("…");
    for (var p = from; p <= to; p++) out.push(p);
    if (to < last - 1) out.push("…");
    out.push(last);
    return out;
  }

  async function renderCredits(panel) {
    panel.innerHTML =
      // v81c (user request): the help text on the LEFT, the search on the RIGHT of one row.
      '<div class="wtp-cr__top">' +
        '<span class="wtp-admin__avhelp">Every player is listed below. Search to narrow the list, then open a player to see their history and add or remove credits with a note.</span>' +
        '<form class="wtp-cr__search" autocomplete="off">' +
          '<input type="search" name="q" placeholder="Find a player by email or name" aria-label="Find a player">' +
          '<button type="submit">Search</button>' +
        "</form>" +
      "</div>" +
      '<div class="wtp-cr__flagged"></div>' +
      '<div class="wtp-cr__player"></div>' +
      '<div class="wtp-cr__count" role="status"></div>' +
      '<div class="wtp-cr__results"></div>' +
      '<nav class="wtp-cr__pager" aria-label="Player pages" hidden></nav>';
    var form = panel.querySelector(".wtp-cr__search");
    var qInput = form.querySelector('input[name="q"]');   // standard lookup; the form.q shortcut is not standard (jsdom lacks it)
    var count = panel.querySelector(".wtp-cr__count");
    var results = panel.querySelector(".wtp-cr__results");
    var pager = panel.querySelector(".wtp-cr__pager");
    var player = panel.querySelector(".wtp-cr__player");
    renderFlagged(panel.querySelector(".wtp-cr__flagged"));

    function rowHtml(p) {
      return '<tr data-email="' + esc(p.email) + '"><td><strong>' + esc(p.username || "(no name)") + "</strong>" +
        '<div class="wtp-cr__email">' + esc(p.email) + "</div></td>" +
        '<td class="wtp-cr__bal">' + crNum(p.wallet) + (p.unlimited ? ' <span class="wtp-cr__unl">∞ Unlimited</span>' : "") + "</td>" +
        "<td>" + crNum(p.stash) + "/20</td>" +
        "<td>" + (p.last_seen ? esc(new Date(p.last_seen).toLocaleDateString()) : "—") + "</td>" +
        '<td><button type="button" class="wtp-cr__open" data-email="' + esc(p.email) + '">Open</button></td></tr>';
    }

    function pagerHtml(cur, last) {
      var b = function (label, page, cls, extra) {
        return '<button type="button" class="wtp-cr__pg' + (cls ? " " + cls : "") + '" data-page="' + page + '"' + (extra || "") + ">" + label + "</button>";
      };
      return b("‹ Prev", cur - 1, "wtp-cr__pg--nav", cur <= 1 ? " disabled" : "") +
        pageList(cur, last).map(function (p) {
          return p === "…" ? '<span class="wtp-cr__gap" aria-hidden="true">…</span>'
            : b(String(p), p, p === cur ? "is-on" : "", (p === cur ? ' aria-current="page" disabled' : "") + ' aria-label="Page ' + p + '"');
        }).join("") +
        b("Next ›", cur + 1, "wtp-cr__pg--nav", cur >= last ? " disabled" : "");
    }

    var page = 1, query = "", seq = 0;
    async function load(q, pg) {
      var my = ++seq;                                    // only the newest request may paint
      query = q; page = Math.max(1, pg || 1);
      pager.querySelectorAll("button").forEach(function (b) { b.disabled = true; });
      if (!results.querySelector("tbody")) {
        results.innerHTML = '<div class="wtp-admin__loading">' + (q ? "Searching…" : "Loading players…") + "</div>";
      } else {
        results.classList.add("is-busy");                // keep the old page on screen until the new one arrives
      }
      var r = null;
      try { r = await sb.rpc("admin_list_players", { p_query: q, p_limit: CR_PAGE, p_offset: (page - 1) * CR_PAGE }); } catch (err) {}
      if (my !== seq) return;                            // a newer search or page click came in meanwhile
      results.classList.remove("is-busy");
      var d = r && r.data;
      if (!d || !d.ok) {
        results.innerHTML = '<div class="wtp-admin__error">' + (d && d.error === "not_admin"
          ? "This account isn’t an admin." : "Couldn’t load the players — please try again.") + "</div>";
        pager.hidden = true; count.textContent = "";
        return;
      }
      var last = Math.max(1, Math.ceil(d.total / CR_PAGE));
      if (!d.items.length && d.total > 0 && page > 1) return load(q, last);   // the list shrank: go to its last page
      results.innerHTML = d.items.length
        ? '<table class="wtp-cr__table"><thead><tr><th>Player</th><th>Credits</th><th>Stash</th><th>Last active</th><th></th></tr></thead><tbody>' +
          d.items.map(rowHtml).join("") + "</tbody></table>"
        : '<p class="wtp-admin__avempty">' + (q ? "No player matches “" + esc(q) + "”." : "No players yet.") + "</p>";
      var a = (page - 1) * CR_PAGE + 1, z = a + d.items.length - 1;
      var n = d.total + " player" + (d.total === 1 ? "" : "s") + (q ? " matching “" + q + "”" : "");
      count.textContent = !d.total ? "" : d.total <= CR_PAGE ? n : "Showing " + a + "–" + z + " of " + n;
      pager.innerHTML = pagerHtml(page, last);
      pager.hidden = last <= 1;
    }

    form.addEventListener("submit", function (e) { e.preventDefault(); load(qInput.value.trim(), 1); });
    var typing = null;                                   // filter as the admin types
    qInput.addEventListener("input", function () {
      clearTimeout(typing);
      var q = qInput.value.trim();
      typing = setTimeout(function () { if (q !== query) load(q, 1); }, 300);
    });
    pager.addEventListener("click", async function (e) {
      var b = e.target.closest("button[data-page]");
      if (!b || b.disabled) return;
      await load(query, Number(b.getAttribute("data-page")));
      try { count.scrollIntoView({ behavior: "smooth", block: "start" }); } catch (err) {}
    });
    results.addEventListener("click", async function (e) {
      var b = e.target.closest(".wtp-cr__open");
      if (!b) return;
      results.querySelectorAll("tr.is-open").forEach(function (tr) { tr.classList.remove("is-open"); });
      b.closest("tr").classList.add("is-open");
      await renderPlayer(player, b.getAttribute("data-email"));
      try { player.scrollIntoView({ behavior: "smooth", block: "start" }); } catch (err) {}
    });
    // A credit change on the card updates that player's row in the list too.
    player.onAdjusted = function (email, balance) {
      results.querySelectorAll("tr[data-email]").forEach(function (tr) {
        if (tr.getAttribute("data-email") !== email) return;
        var cell = tr.querySelector(".wtp-cr__bal");
        if (cell) cell.firstChild.nodeValue = crNum(balance) + (cell.children.length ? " " : "");
      });
    };

    load("", 1);                                         // open on the full list, page 1
  }

  async function renderFlagged(box) {
    var r = null;
    try { r = await sb.rpc("admin_flagged_orders"); } catch (e) {}
    var d = r && r.data;
    if (!d || !d.ok || !d.items.length) { box.innerHTML = ""; return; }
    box.innerHTML = '<div class="wtp-cr__flag"><h3>Needs a refund (' + d.items.length + ")</h3>" +
      '<p>These customers bought the First Purchase Offer again. It’s one per account, so no credits were added — refund these orders in Shopify.</p><ul>' +
      d.items.map(function (f) {
        return "<li><strong>Order " + esc(f.order_number ? "#" + f.order_number : f.order) + "</strong> · " + esc(f.email) +
          " · " + esc(new Date(f.at).toLocaleString()) + "</li>";
      }).join("") + "</ul></div>";
  }

  async function renderPlayer(box, email, before, keep) {
    if (!keep) box.innerHTML = '<div class="wtp-admin__loading">Loading ' + esc(email) + "…</div>";
    var r = null;
    try { r = await sb.rpc("admin_player_credits", { p_email: email, p_limit: 40, p_before: before || null }); } catch (e) {}
    var d = r && r.data;
    if (!d || !d.ok) { box.innerHTML = '<div class="wtp-admin__error">Couldn’t load this player.</div>'; return; }
    var rows = d.items.map(function (it) {
      var dt = it.detail || {}, delta = Number(it.delta);
      var what = REASON[it.reason] || it.reason;
      var extra = it.reason === "admin_adjust" && dt.note ? "“" + dt.note + "”" + (dt.by ? " — " + dt.by : "")
        : it.order_number ? "Order #" + it.order_number
        : dt.item ? ADDON[dt.item] || words(dt.item)
        : dt.category ? (COSMETIC[dt.category] || words(dt.category)) + (dt.items && dt.items.length ? " · " + dt.items.length + " item" + (dt.items.length === 1 ? "" : "s") : "")
        : dt.pack ? words(dt.pack) + " pack" : "";
      return "<tr><td>" + esc(new Date(it.at).toLocaleString()) + "</td><td>" + esc(what) +
        (extra ? '<div class="wtp-cr__email">' + esc(extra) + "</div>" : "") + "</td>" +
        "<td>" + (it.pool === "stash" ? "Stash" : "Wallet") + "</td>" +
        '<td class="' + (delta > 0 ? "is-plus" : delta < 0 ? "is-minus" : "") + '">' + (delta > 0 ? "+" : "") + crNum(delta) + "</td></tr>";
    }).join("");
    var head = keep ? "" :
      '<div class="wtp-cr__card"><div class="wtp-cr__who"><h3>' + esc(d.username || email) + "</h3>" +
        '<div class="wtp-cr__email">' + esc(email) + "</div></div>" +
        '<div class="wtp-cr__kpis"><div><span>' + crNum(d.wallet) + "</span>credits</div>" +
          "<div><span>" + crNum(d.stash) + "/20</span>Stash Box</div>" +
          "<div><span>" + (d.unlimited ? "∞ Active" : "—") + "</span>Unlimited" +
            (d.unlimited_until ? " (until " + esc(new Date(d.unlimited_until).toLocaleDateString()) + ")" : "") + "</div></div>" +
        '<form class="wtp-cr__adjust">' +
          '<label>Amount <input name="amt" type="number" step="1" min="1" max="1000" value="1" required></label>' +
          '<label class="wtp-cr__note">Note (required) <input name="note" maxlength="200" placeholder="e.g. Goodwill — loading bug" required></label>' +
          '<button type="submit" name="act" value="add">Add credits</button>' +
          '<button type="submit" name="act" value="remove" class="is-danger">Remove credits</button>' +
          '<span class="wtp-cr__msg" role="status"></span>' +
        "</form></div>" +
        '<h3 class="wtp-cr__h">History</h3><table class="wtp-cr__table wtp-cr__hist"><thead><tr><th>When</th><th>What</th><th>Pool</th><th>Change</th></tr></thead><tbody></tbody></table>' +
        '<button type="button" class="wtp-cr__more" hidden>Show more</button>';
    if (!keep) box.innerHTML = head;
    box.querySelector(".wtp-cr__hist tbody").insertAdjacentHTML("beforeend", rows ||
      (keep ? "" : '<tr><td colspan="4">No credit history yet.</td></tr>'));
    var more = box.querySelector(".wtp-cr__more");
    more.hidden = !d.more;
    more.onclick = function () {
      more.hidden = true;
      renderPlayer(box, email, d.items.length ? d.items[d.items.length - 1].at : null, true);
    };
    if (keep) return;

    var form = box.querySelector(".wtp-cr__adjust"), msg = box.querySelector(".wtp-cr__msg"), act = "add";
    form.querySelectorAll("button[name=act]").forEach(function (b) {
      b.addEventListener("click", function () { act = b.value; });
    });
    form.addEventListener("submit", async function (e) {
      e.preventDefault();
      var amt = Math.round(Number(form.amt.value)), note = form.note.value.trim();
      if (!(amt >= 1 && amt <= 1000) || !note) return;
      var delta = act === "remove" ? -amt : amt;
      if (!window.confirm((delta > 0 ? "Add " : "Remove ") + amt + " credit" + (amt === 1 ? "" : "s") +
          (delta > 0 ? " to " : " from ") + email + "?\n\nNote: " + note)) return;
      form.querySelectorAll("button").forEach(function (b) { b.disabled = true; });
      var r2 = null;
      try { r2 = await sb.rpc("admin_adjust_credits", { p_email: email, p_delta: delta, p_note: note }); } catch (err) {}
      var res = r2 && r2.data;
      form.querySelectorAll("button").forEach(function (b) { b.disabled = false; });
      if (res && res.ok) {
        if (box.onAdjusted) box.onAdjusted(email, res.balance);   // v81: keep the list's row in step
        return renderPlayer(box, email);
      }
      msg.textContent = {
        would_go_negative: "They only have " + crNum(res && res.balance) + " credits — you can’t remove more than that.",
        note_required: "Please add a note.", note_too_long: "Keep the note under 200 characters.",
        bad_amount: "Amount must be between 1 and 1000.", not_admin: "This account isn’t an admin.",
      }[res && res.error] || "That didn’t go through — please try again.";
    });
  }

  // ---- M14: analytics ("advanced analytics", Phase 2) --------------------------
  // No client definition exists for this item, so it measures the v2 features
  // the client is paying for — what earns and what gets used. Everything comes
  // from admin_analytics(), which checks is_admin() and returns counts only.
  //
  // v82 design (user request 2026-10-07: "improve the analytics page design and somewhere
  // use the ring bar"), following the data-viz rules:
  // - a ring (donut) only for part-to-whole with ≤ 6 slices, and a ring GAUGE for a single rate;
  //   rankings and per-day counts stay bars;
  // - one brand palette in a FIXED order, each entity always the same colour (never by
  //   rank). It is validated for colour blindness: worst adjacent CVD ΔE 10.9, normal 20.4.
  //   Gold is under 3:1 on white, so every ring prints its values in the legend;
  // - every value is printed (legend, bar tip, table view), and the hover tooltip only adds detail.
  var DIFF = { 2: "Very Easy", 3: "Easy", 4: "Casual", 5: "Medium", 6: "Tricky", 7: "Hard", 8: "Expert" };
  var SOURCE = { purchase: "Credit packs bought", physical_purchase: "Rewards from puzzle orders",
                 share: "Share rewards", stash_unlock: "Stash Box unlocks" };
  var ADDON = { edge_lord: "Edge Lord", cheat_peek: "Cheat Peek", auto_sort: "Auto-Sort Edge Pieces" };
  var COSMETIC = { confetti: "Confetti Cannon", sound: "Snap Pack", foil: "Gold Foil Finish", cursor: "Custom Cursor" };
  var PLATFORM = { facebook: "Facebook", instagram: "Instagram", snapchat: "Snapchat" };
  function n(v) { v = Math.round(Number(v || 0) * 100) / 100; return v.toLocaleString(); }

  var AN_C = ["#E14F97", "#7A5FD1", "#1E9C8B", "#D9A441", "#2A78D6", "#EB6834"];   // fixed slot order
  // Fixed entity -> slot, so a colour always means the same thing on every period.
  var IN_SLOTS = [["physical_purchase", "Puzzle orders"], ["purchase", "Credit packs"], ["share", "Share rewards"],
                  ["stash_unlock", "Stash Box unlocks"], ["admin_adjust", "Added by admins"]];
  var SPENT_SLOTS = [["edge_lord", "Edge Lord"], ["cheat_peek", "Cheat Peek"], ["auto_sort", "Auto-Sort"], ["cosmetics", "Cosmetics"]];
  function pct(a, b) { return b > 0 ? Math.round(a / b * 100) : 0; }
  function tipAttrs(value, label) {     // read by the tooltip with textContent, never as HTML
    return ' tabindex="0" data-tip-v="' + esc(value) + '" data-tip-l="' + esc(label) + '"';
  }
  function none(text) { return '<p class="wtp-an__none">' + esc(text || "Nothing yet in this period.") + "</p>"; }

  // Horizontal bars (one series = one colour), values at the tip. rows: [label, value, sub?]
  function bars(rows, unit, keepOrder) {
    rows = rows.filter(function (r) { return Number(r[1]) > 0; });
    if (!keepOrder) rows.sort(function (a, b) { return Number(b[1]) - Number(a[1]); });
    if (!rows.length) return none();
    var max = Math.max.apply(null, rows.map(function (r) { return Number(r[1]); }));
    return '<div class="wtp-an__bars">' + rows.map(function (r) {
      return '<div class="wtp-an__bar"' + tipAttrs(n(r[1]) + (unit ? " " + unit : ""), r[0] + (r[2] ? " · " + r[2] : "")) + ">" +
        '<span class="l">' + esc(r[0]) + "</span>" +
        '<span class="t"><span class="f" style="width:' + Math.max(3, Math.round(Number(r[1]) / max * 100)) + '%"></span></span>' +
        '<span class="v">' + n(r[1]) + (unit ? " " + esc(Number(r[1]) === 1 ? unit.replace(/s$/, "") : unit) : "") +   // "1 share", "2 shares"
        (r[2] ? " <small>" + esc(r[2]) + "</small>" : "") + "</span></div>";
    }).join("") + "</div>";
  }

  // Part-to-whole ring. rows: [{label, value, color, sub}] in the FIXED entity order.
  function donut(rows, centreLabel, unit, title) {
    rows = rows.filter(function (r) { return Number(r.value) > 0; });
    if (!rows.length) return none();
    var total = rows.reduce(function (s, r) { return s + Number(r.value); }, 0);
    var R = 52, SW = 16, C = 2 * Math.PI * R, gap = rows.length > 1 ? 2 : 0, off = 0;
    var segs = rows.map(function (r) {
      var len = Number(r.value) / total * C, dash = Math.max(0.6, len - gap);
      var s = '<circle class="wtp-an__seg" cx="64" cy="64" r="' + R + '" fill="none" stroke="' + r.color + '" stroke-width="' + SW + '" ' +
        'stroke-dasharray="' + dash.toFixed(2) + " " + (C - dash).toFixed(2) + '" stroke-dashoffset="' + (-off).toFixed(2) + '" ' +
        'transform="rotate(-90 64 64)"' + tipAttrs(n(r.value) + " " + unit + " · " + pct(r.value, total) + "%", r.label) +
        ' aria-label="' + esc(r.label + ": " + n(r.value) + " " + unit) + '"></circle>';
      off += len;
      return s;
    }).join("");
    return '<div class="wtp-an__donut">' +
      '<div class="wtp-an__ring"><svg viewBox="0 0 128 128" width="128" height="128" role="img" aria-label="' + esc(title) + '">' +
        segs + "</svg>" +
        '<div class="wtp-an__ring-c"><strong>' + n(total) + "</strong><span>" + esc(centreLabel) + "</span></div></div>" +
      '<ul class="wtp-an__legend">' + rows.map(function (r) {
        return '<li><i style="background:' + r.color + '"></i><span class="k">' + esc(r.label) +
          (r.sub ? "<small>" + esc(r.sub) + "</small>" : "") + "</span>" +
          '<span class="v">' + n(r.value) + " " + esc(unit) + "<small>" + pct(r.value, total) + "%</small></span></li>";
      }).join("") + "</ul></div>";
  }

  // A single rate as a ring gauge (a circular meter: brand pink on a pale-pink track).
  function gauge(value, total, title, detail) {
    var p = total > 0 ? Math.max(0, Math.min(1, value / total)) : 0, R = 34, C = 2 * Math.PI * R;
    return '<div class="wtp-an__gauge"' + tipAttrs(total > 0 ? pct(value, total) + "%" : "—", title + " · " + detail) + ">" +
      '<div class="wtp-an__ring wtp-an__ring--sm"><svg viewBox="0 0 84 84" width="84" height="84" role="img" aria-label="' +
        esc(title + ": " + (total > 0 ? pct(value, total) + "%" : "no data")) + '">' +
        '<circle cx="42" cy="42" r="' + R + '" fill="none" stroke="#F6E4EE" stroke-width="9"></circle>' +
        (p > 0 ? '<circle cx="42" cy="42" r="' + R + '" fill="none" stroke="#E14F97" stroke-width="9" stroke-linecap="round" ' +
          'stroke-dasharray="' + (p * C).toFixed(2) + " " + C.toFixed(2) + '" transform="rotate(-90 42 42)"></circle>' : "") +
        "</svg>" +
        '<div class="wtp-an__ring-c"><strong>' + (total > 0 ? pct(value, total) + "%" : "—") + "</strong></div></div>" +
      '<div class="wtp-an__gauge-t"><strong>' + esc(title) + "</strong><span>" + esc(detail) + "</span></div></div>";
  }

  // "Nice" y-axis: 4-5 round ticks from 0 to just above the max.
  function niceTicks(max) {
    if (max <= 5) { var t = []; for (var i = 0; i <= Math.max(1, max); i++) t.push(i); return t; }
    var raw = max / 4, mag = Math.pow(10, Math.floor(Math.log10(raw))), step = mag;
    [1, 2, 5, 10].some(function (m) { step = m * mag; return step >= raw; });
    var out = [];
    for (var v = 0; v < max + step; v += step) out.push(v);
    return out;
  }
  function shortDate(iso) {
    return new Date(iso + "T00:00:00Z").toLocaleDateString(undefined, { month: "short", day: "numeric", timeZone: "UTC" });
  }

  // Columns per day: y-axis + hairline grid, ≤ 24px columns with a 4px rounded top, the
  // busiest day labelled, a tooltip per column, and a table view.
  function dailyChart(cols, span) {
    var max = Math.max.apply(null, cols.map(function (c) { return c[1]; }));
    var total = cols.reduce(function (s, c) { return s + c[1]; }, 0);
    if (!total) return none("No puzzles finished in this period.");
    var ticks = niceTicks(max), top = ticks[ticks.length - 1];
    var peak = cols.reduce(function (b, c) { return c[1] > b[1] ? c : b; }, cols[0]);
    var mid = Math.floor((cols.length - 1) / 2);
    return '<p class="wtp-an__sub">' + n(total) + " finished in the last " + span + " days · busiest day " + esc(shortDate(peak[0])) +
        " (" + n(peak[1]) + ") · " + n(Math.round(total / span * 10) / 10) + " a day on average</p>" +
      '<div class="wtp-an__cols-wrap">' +
        '<div class="wtp-an__yaxis">' + ticks.map(function (t) {
          return '<span style="bottom:' + (t / top * 100) + '%">' + n(t) + "</span>";
        }).join("") + "</div>" +
        '<div class="wtp-an__plot">' +
          ticks.map(function (t) { return '<i class="wtp-an__grid-l" style="bottom:' + (t / top * 100) + '%"></i>'; }).join("") +
          '<div class="wtp-an__cols" role="img" aria-label="Puzzles completed per day">' + cols.map(function (c) {
            var h = c[1] ? Math.max(2, c[1] / top * 100) : 0;
            return '<span class="wtp-an__col"' + tipAttrs(n(c[1]) + " finished", shortDate(c[0])) + ">" +
              (c === peak && c[1] ? '<b class="wtp-an__peak" style="bottom:calc(' + h + '% + 4px)">' + n(c[1]) + "</b>" : "") +
              '<span class="f" style="height:' + h + '%"></span></span>';
          }).join("") + "</div>" +
        "</div>" +
      "</div>" +
      '<div class="wtp-an__xaxis"><span>' + esc(shortDate(cols[0][0])) + "</span><span>" + esc(shortDate(cols[mid][0])) +
        "</span><span>" + esc(shortDate(cols[cols.length - 1][0])) + "</span></div>" +
      '<button type="button" class="wtp-an__tbl-btn" aria-expanded="false">View as table</button>' +
      '<table class="wtp-an__tbl" hidden><thead><tr><th>Day</th><th>Puzzles finished</th></tr></thead><tbody>' +
        cols.slice().reverse().filter(function (c) { return c[1] > 0; }).map(function (c) {
          return "<tr><td>" + esc(shortDate(c[0])) + "</td><td>" + n(c[1]) + "</td></tr>";
        }).join("") + "</tbody></table>";
  }

  function sparkline(cols) {
    var max = Math.max(1, Math.max.apply(null, cols.map(function (c) { return c[1]; }))), W = 120, Hh = 28;
    var pts = cols.map(function (c, i) {
      return (i / (cols.length - 1) * W).toFixed(1) + "," + (Hh - 2 - c[1] / max * (Hh - 4)).toFixed(1);
    });
    return '<svg class="wtp-an__spark" viewBox="0 0 ' + W + " " + Hh + '" preserveAspectRatio="none" aria-hidden="true">' +
      '<polyline points="0,' + Hh + " " + pts.join(" ") + " " + W + "," + Hh + '" fill="#E14F97" fill-opacity="0.1" stroke="none"></polyline>' +
      '<polyline points="' + pts.join(" ") + '" fill="none" stroke="#E14F97" stroke-width="2" stroke-linejoin="round" stroke-linecap="round" vector-effect="non-scaling-stroke"></polyline></svg>';
  }

  function stats(rows) {               // a tidy list of labelled numbers
    return '<dl class="wtp-an__dl">' + rows.map(function (r) {
      return "<div><dt>" + esc(r[0]) + "</dt><dd>" + r[1] + "</dd></div>";
    }).join("") + "</dl>";
  }

  // One tooltip for the whole tab: hover or keyboard focus on any [data-tip-v] mark.
  function wireTooltip(panel) {
    if (!panel.querySelector(".wtp-an__tip")) {
      panel.appendChild(el('<div class="wtp-an__tip" role="status"><strong></strong><span></span></div>'));
    }
    if (panel._tipWired) return;      // the listeners outlive each re-render; attach them once
    panel._tipWired = true;
    function tipEl() { return panel.querySelector(".wtp-an__tip"); }
    function show(t, x, y) {
      var tip = tipEl();
      if (!tip) return;
      tip.firstChild.textContent = t.getAttribute("data-tip-v");
      tip.lastChild.textContent = t.getAttribute("data-tip-l");
      var pr = panel.getBoundingClientRect();
      tip.style.left = Math.max(4, Math.min(pr.width - 180, x - pr.left + 12)) + "px";
      tip.style.top = (y - pr.top + 14) + "px";
      tip.classList.add("is-on");
    }
    function hide() { var tip = tipEl(); if (tip) tip.classList.remove("is-on"); }
    panel.addEventListener("pointermove", function (e) {
      var t = e.target.closest && e.target.closest("[data-tip-v]");
      if (t && panel.contains(t)) show(t, e.clientX, e.clientY); else hide();
    });
    panel.addEventListener("pointerleave", hide);
    // focusin/focusout via addEventListener: Chrome has no `onfocusin` property, so keyboard
    // users would get no tooltip at all if this were assigned as panel.onfocusin.
    panel.addEventListener("focusin", function (e) {
      var t = e.target.closest && e.target.closest("[data-tip-v]");
      if (!t) return hide();
      var r = t.getBoundingClientRect();
      show(t, r.left + r.width / 2, r.bottom);
    });
    panel.addEventListener("focusout", hide);
  }

  async function renderAnalytics(panel, days) {
    // Refetch keeps the frame: an existing dashboard fades while the new period loads.
    if (panel.querySelector(".wtp-an__grid")) panel.classList.add("is-busy");
    else panel.innerHTML = '<div class="wtp-admin__loading">Loading analytics…</div>';
    var res = null, titles = {}, packs = {};
    try {
      var got = await Promise.all([
        sb.rpc("admin_analytics", { p_days: days }),
        fetchAllProducts().catch(function () { return []; }),
        sb.from("credit_packs").select("code, label"),
      ]);
      res = got[0];
      got[1].forEach(function (p) { titles[String(p.id)] = String(p.title || "").replace(/^\s*digital\s*[|:\-]\s*/i, ""); });
      ((got[2] && got[2].data) || []).forEach(function (p) { packs[p.code] = p.label; });
    } catch (e) {}
    panel.classList.remove("is-busy");
    var d = res && res.data;
    if (!d || !d.ok) {
      panel.innerHTML = '<div class="wtp-admin__error">Couldn’t load analytics' +
        (d && d.error === "not_admin" ? " — this account isn’t an admin." : ".") + "</div>";
      return;
    }
    var pl = d.play, cr = d.credits, su = d.subscription, so = d.social, co = d.community;
    var periods = [[7, "Last 7 days"], [30, "Last 30 days"], [0, "All time"]];
    var periodName = days > 0 ? "last " + days + " days" : "all time";
    var inBy = cr.in_by_source || {};
    var sold = Number(inBy.purchase || 0), earnedOrders = Number(inBy.physical_purchase || 0);

    // Daily completions (the last 30 days at most), zero-filled. The headline numbers count a
    // ROLLING window (now − N days), and the server's per-day rows start inside that window's first
    // calendar day, so the chart starts on that date too (a partial first day): N + 1 columns whose
    // total equals "Puzzles completed". With N columns, finishes on the first day went missing.
    var dayMap = {}; (pl.daily || []).forEach(function (x) { dayMap[x.day] = x.completions; });
    var span = days > 0 ? Math.min(days, 30) : 30, cols = [];
    for (var i = span; i >= 0; i--) {
      var dt = new Date(Date.now() - i * 86400000).toISOString().slice(0, 10);
      cols.push([dt, Number(dayMap[dt] || 0)]);
    }

    var kpi = function (label, value, hint, extra) {
      return '<div class="wtp-an__kpi"><div class="l">' + esc(label) + '</div><div class="v">' + value + "</div>" +
        (hint ? '<div class="h">' + esc(hint) + "</div>" : "") + (extra || "") + "</div>";
    };

    // Credits in: fixed slots; anything else positive (e.g. test grants) folds into "Other".
    var known = {}; IN_SLOTS.forEach(function (s) { known[s[0]] = 1; });
    var otherIn = Object.keys(inBy).reduce(function (s, k) { return known[k] ? s : s + Number(inBy[k] || 0); }, 0);
    var inRows = IN_SLOTS.map(function (s, k) { return { label: s[1], value: Number(inBy[s[0]] || 0), color: AN_C[k] }; })
      .concat([{ label: "Other", value: otherIn, color: AN_C[5] }]);

    // Credits spent: the three helpers, then every cosmetic shop as one "Cosmetics" slice.
    var sa = cr.spent_addons || {}, sc = cr.spent_cosmetics || {};
    var cosTotal = 0, cosParts = [];
    Object.keys(sc).forEach(function (k) {
      cosTotal += Number(sc[k].credits || 0);
      if (Number(sc[k].credits) > 0) cosParts.push((COSMETIC[k] || k) + " " + n(sc[k].credits));
    });
    var spentRows = SPENT_SLOTS.map(function (s, k) {
      var v = s[0] === "cosmetics" ? cosTotal : Number((sa[s[0]] || {}).credits || 0);
      var sub = s[0] === "cosmetics" ? cosParts.join(" · ")
        : (sa[s[0]] ? n(sa[s[0]].uses) + " use" + (Number(sa[s[0]].uses) === 1 ? "" : "s") : "");
      return { label: s[1], value: v, color: AN_C[k], sub: sub };
    });

    var shares = Object.keys(so.shares_by_platform || {}).reduce(function (s, k) { return s + Number(so.shares_by_platform[k] || 0); }, 0);
    var created = Number(so.challenges_created || 0), plays = Number(so.challenge_plays || 0), wins = Number(so.challenge_wins || 0);

    panel.innerHTML =
      '<div class="wtp-an__bar-top">' + periods.map(function (p) {
        return '<button type="button" class="wtp-admin__avf' + (p[0] === days ? " is-on" : "") + '" data-days="' + p[0] + '">' + p[1] + "</button>";
      }).join("") + '<span class="wtp-admin__avhelp">Counts from the store’s own records — no third-party tracking.</span></div>' +

      '<div class="wtp-an__kpis">' +
        kpi("Puzzles completed", n(pl.completions), periodName, sparkline(cols)) +
        kpi("Players", n(pl.players), "finished a puzzle") +
        kpi("Credits earned from orders", n(earnedOrders), "5 per $24 of puzzles") +
        kpi("Credits sold in packs", n(sold), sold ? periodName : "none sold yet") +
        kpi("Credits spent on add-ons", n(cr.spent_total), "helpers + cosmetics") +
        kpi("Unlimited subscribers", n(su.active_now), "active now") +
      "</div>" +

      '<div class="wtp-an__grid">' +
        '<section class="wtp-an__card wtp-an__card--wide"><h3>Puzzles completed per day</h3>' + dailyChart(cols, span) + "</section>" +

        '<section class="wtp-an__card"><h3>Top puzzles</h3>' + bars((pl.top_puzzles || []).map(function (t) {
          return [titles[String(t.product)] || ("Product " + t.product), t.completions, t.players + " player" + (t.players === 1 ? "" : "s")];
        })) + "</section>" +
        '<section class="wtp-an__card"><h3>Completions by difficulty</h3>' + bars(Object.keys(DIFF).map(function (k) {
          return [DIFF[k], (pl.by_difficulty || {})[k] || 0];
        }), null, true) + "</section>" +
        '<section class="wtp-an__card"><h3>Sharing</h3>' +
          gauge(shares, Number(pl.completions || 0), "Finished puzzles shared", n(shares) + " of " + n(pl.completions) + " puzzles") +
          bars(Object.keys(PLATFORM).map(function (k) { return [PLATFORM[k], (so.shares_by_platform || {})[k] || 0]; }), "shares") +
        "</section>" +

        '<section class="wtp-an__card"><h3>Credits in</h3>' + donut(inRows, "credits in", "cr", "Credits in by source") +
          (Number(cr.refunded) > 0 ? '<p class="wtp-an__note">Refunded (taken back): ' + n(cr.refunded) + " cr</p>" : "") + "</section>" +
        '<section class="wtp-an__card"><h3>Credits spent</h3>' + donut(spentRows, "credits spent", "cr", "Credits spent by add-on") +
          (Number(cr.unlimited_uses) > 0 ? '<p class="wtp-an__note">Plus ' + n(cr.unlimited_uses) + " add-ons used free on Unlimited.</p>" : "") + "</section>" +
        '<section class="wtp-an__card"><h3>Challenge a Friend</h3>' +
          (created ? '<div class="wtp-an__gauges">' +
            gauge(plays, created, "Links played", n(plays) + " of " + n(created) + " links") +
            gauge(wins, plays, "Friends who won", plays ? n(wins) + " of " + n(plays) + " games" : "no games yet") +
          "</div>" : none("No challenge links yet.")) +
        "</section>" +

        '<section class="wtp-an__card"><h3>Pack sales</h3>' + (Object.keys(cr.pack_sales || {}).length
          ? bars(Object.keys(cr.pack_sales).map(function (k) {
              var v = cr.pack_sales[k]; return [packs[k] || k, v.lines, n(v.credits) + " cr"]; }), "sold")
          : none("No packs sold yet — they go on sale once their Shopify products exist.")) + "</section>" +
        '<section class="wtp-an__card"><h3>Stash Box &amp; subscription</h3>' + stats([
          ["Locked Credits awarded", n(cr.stash_awarded)],
          ["Stash Box unlocks ($3.99)", n(cr.stash_unlocks)],
          ["Players with a wallet", n(cr.wallets)],
          ["Unlimited orders (incl. renewals)", n(su.orders)],
          ["Unlimited refunds", n(su.refunded)],
        ]) + "</section>" +
        '<section class="wtp-an__card"><h3>Avatars</h3>' + stats([
          ["Pictures showing now", n(co.avatars_now)],
          ["New pictures", n(co.avatars_new)],
          ["Reports", n(co.reports)],
          ["Hidden, awaiting review", n(co.hidden_now)],
          ["Removed", n(co.removed)],
          ["Players blocked from uploading", n(co.blocked_now)],
        ]) + "</section>" +
      "</div>" +
      '<p class="wtp-an__foot">Share clicks also show in Shopify Analytics as <code>utm_campaign=puzzle_share</code>. ' +
      "Updated " + esc(new Date(d.generated_at).toLocaleString()) + ".</p>";

    wireTooltip(panel);
    panel.onclick = function (e) {
      var b = e.target.closest("[data-days]");
      if (b) return renderAnalytics(panel, Number(b.getAttribute("data-days")));
      var tb = e.target.closest(".wtp-an__tbl-btn");
      if (tb) {
        var tbl = tb.nextElementSibling, open = tbl.hidden;
        tbl.hidden = !open;
        tb.setAttribute("aria-expanded", open ? "true" : "false");
        tb.textContent = open ? "Hide table" : "View as table";
      }
    };
  }

  // ---- M12: avatar moderation -------------------------------------------------
  // Client: "Optional moderation or reporting can be added" (Phase 2). Players
  // report pictures from the leaderboards; 3 different reports hide one at once.
  // Here an admin reviews them: Keep, Remove, or Remove & block uploads. All
  // server calls check is_admin() themselves.
  async function refreshAvatarCount() {
    try {
      var res = await sb.rpc("admin_avatars", { p_filter: "review" });
      var n = res && res.data && res.data.ok ? res.data.items.length : 0;
      var c = root.querySelector(".wtp-admin__count");
      if (c) { c.textContent = n; c.hidden = !n; }
    } catch (e) { /* the badge is a nicety */ }
  }

  function avatarUrl(path, v) {
    try { return sb.storage.from("avatars").getPublicUrl(path).data.publicUrl + "?v=" + v; } catch (e) { return ""; }
  }

  // v83 (user request 2026-10-07, "as we did it for the credits"): the tab opens on EVERY
  // picture, with no filter click needed. "Needs review" stays as a filter with its count, a
  // search box on the right narrows by name or email as you type, and there are 12 pictures
  // a page with the same pager as the Credits tab. The list is small, so search and paging
  // run in the browser over admin_avatars()'s answer; there is no DB change.
  var AV_PAGE = 12;
  function avatarCardHtml(a) {
    var state = a.status === "hidden" ? '<span class="wtp-av-badge is-hidden">Hidden — needs review</span>'
      : a.status === "approved" ? '<span class="wtp-av-badge is-ok">Approved</span>'
      : '<span class="wtp-av-badge">Visible</span>';
    var reports = Number(a.reports) > 0
      ? '<div class="wtp-av-reports">' + a.reports + " report" + (a.reports === 1 ? "" : "s") +
        (a.reasons && a.reasons.length ? " · " + a.reasons.map(esc).join(", ") : "") + "</div>"
      : "";
    return '<div class="wtp-av-card' + (a.status === "hidden" ? " is-hidden" : "") + '" data-email="' + esc(a.email) +
        '" data-path="' + esc(a.path) + '">' +
      '<img src="' + esc(avatarUrl(a.path, a.v)) + '" alt="" loading="lazy">' +
      '<div class="wtp-av-info"><div class="wtp-av-name">' + esc(a.username || "(no name)") + "</div>" +
        '<div class="wtp-av-email">' + esc(a.email) + "</div>" +
        '<div class="wtp-av-when">Uploaded ' + esc(new Date(a.updated_at).toLocaleString()) + "</div>" +
        state + reports + "</div>" +
      '<div class="wtp-av-actions">' +
        '<button type="button" data-act="keep">Keep</button>' +
        '<button type="button" data-act="remove" class="is-danger">Remove</button>' +
        '<button type="button" data-act="remove_block" class="is-danger">Remove &amp; block uploads</button>' +
      "</div></div>";
  }

  async function renderAvatars(panel, filter, keep) {
    filter = filter || "all";
    var view = { q: keep ? keep.q : "", page: keep ? keep.page : 1 };
    // Keep the old view (faded) while reloading, instead of flashing "Loading".
    if (panel.querySelector(".wtp-av__list")) panel.classList.add("is-busy");
    else panel.innerHTML = '<div class="wtp-admin__loading">Loading pictures…</div>';
    var res = null, rev = null;
    try {
      var got = await Promise.all([
        sb.rpc("admin_avatars", { p_filter: filter }),
        filter === "review" ? Promise.resolve(null) : sb.rpc("admin_avatars", { p_filter: "review" }),
      ]);
      res = got[0]; rev = got[1];
    } catch (e) {}
    panel.classList.remove("is-busy");
    var d = res && res.data;
    if (!d || !d.ok) {
      panel.innerHTML = '<div class="wtp-admin__error">Couldn’t load pictures' +
        (d && d.error === "not_admin" ? " — this account isn’t an admin." : ".") + "</div>";
      return;
    }
    var items = d.items || [];
    var reviewN = filter === "review" ? items.length : (rev && rev.data && rev.data.ok ? rev.data.items.length : 0);

    panel.innerHTML =
      '<div class="wtp-av__top">' +
        '<div class="wtp-av__filters">' +
          '<button type="button" class="wtp-admin__avf' + (filter === "all" ? " is-on" : "") + '" data-f="all">All pictures</button>' +
          '<button type="button" class="wtp-admin__avf' + (filter === "review" ? " is-on" : "") + '" data-f="review">Needs review' +
            (reviewN ? ' <span class="wtp-av__n">' + reviewN + "</span>" : "") + "</button>" +
        "</div>" +
        '<form class="wtp-cr__search wtp-av__search" autocomplete="off">' +
          '<input type="search" name="q" placeholder="Find a picture by player name or email" aria-label="Find a picture" value="' + esc(view.q) + '">' +
          '<button type="submit">Search</button>' +
        "</form>" +
      "</div>" +
      '<p class="wtp-av__help">Players report pictures from the leaderboards. A picture reported by 3 players is hidden until you decide.</p>' +
      '<div class="wtp-av__count" role="status"></div>' +
      '<div class="wtp-av__list"></div>' +
      '<nav class="wtp-cr__pager wtp-av__pager" aria-label="Picture pages" hidden></nav>';

    var form = panel.querySelector(".wtp-av__search"), list = panel.querySelector(".wtp-av__list");
    var qInput = form.querySelector('input[name="q"]');
    var count = panel.querySelector(".wtp-av__count"), pager = panel.querySelector(".wtp-av__pager");

    function paint() {
      var q = view.q.toLowerCase();
      var shown = q ? items.filter(function (a) {
        return String(a.email || "").toLowerCase().indexOf(q) !== -1 || String(a.username || "").toLowerCase().indexOf(q) !== -1;
      }) : items;
      var last = Math.max(1, Math.ceil(shown.length / AV_PAGE));
      view.page = Math.min(Math.max(1, view.page), last);
      var from = (view.page - 1) * AV_PAGE, pageItems = shown.slice(from, from + AV_PAGE);
      list.innerHTML = pageItems.length
        ? '<div class="wtp-av-grid">' + pageItems.map(avatarCardHtml).join("") + "</div>"
        : '<p class="wtp-admin__avempty">' + (q ? "No picture matches “" + esc(view.q) + "”."
            : filter === "review" ? "Nothing to review — no reported or hidden pictures." : "No player pictures yet.") + "</p>";
      var noun = shown.length + " picture" + (shown.length === 1 ? "" : "s") +
        (filter === "review" ? " to review" : "") + (q ? " matching “" + view.q + "”" : "");
      count.textContent = !shown.length ? "" : shown.length <= AV_PAGE ? noun
        : "Showing " + (from + 1) + "–" + (from + pageItems.length) + " of " + noun;
      pager.innerHTML = last > 1 ? pagerButtons(view.page, last) : "";
      pager.hidden = last <= 1;
    }
    function pagerButtons(cur, last) {
      var b = function (label, page, cls, extra) {
        return '<button type="button" class="wtp-cr__pg' + (cls ? " " + cls : "") + '" data-page="' + page + '"' + (extra || "") + ">" + label + "</button>";
      };
      return b("‹ Prev", cur - 1, "wtp-cr__pg--nav", cur <= 1 ? " disabled" : "") +
        pageList(cur, last).map(function (p) {
          return p === "…" ? '<span class="wtp-cr__gap" aria-hidden="true">…</span>'
            : b(String(p), p, p === cur ? "is-on" : "", p === cur ? ' aria-current="page" disabled' : ' aria-label="Page ' + p + '"');
        }).join("") +
        b("Next ›", cur + 1, "wtp-cr__pg--nav", cur >= last ? " disabled" : "");
    }
    paint();

    form.addEventListener("submit", function (e) { e.preventDefault(); view.q = qInput.value.trim(); view.page = 1; paint(); });
    var typing = null;
    qInput.addEventListener("input", function () {
      clearTimeout(typing);
      typing = setTimeout(function () { view.q = qInput.value.trim(); view.page = 1; paint(); }, 200);
    });

    panel.onclick = async function (e) {
      var f = e.target.closest("[data-f]");
      if (f) return renderAvatars(panel, f.getAttribute("data-f"), { q: view.q, page: 1 });
      var pg = e.target.closest(".wtp-av__pager button[data-page]");
      if (pg) {
        if (pg.disabled) return;
        view.page = Number(pg.getAttribute("data-page"));
        paint();
        try { count.scrollIntoView({ behavior: "smooth", block: "start" }); } catch (err) {}
        return;
      }
      var b = e.target.closest("[data-act]");
      if (!b) return;
      var card = b.closest(".wtp-av-card");
      var email = card.getAttribute("data-email"), path = card.getAttribute("data-path");
      var act = b.getAttribute("data-act");
      if (act !== "keep" && !window.confirm(act === "remove_block"
          ? "Remove this picture AND stop " + email + " uploading pictures?"
          : "Remove this picture? The player will see their initial instead.")) return;
      card.querySelectorAll("button").forEach(function (x) { x.disabled = true; });
      try {
        if (act !== "keep") await sb.storage.from("avatars").remove([path]);   // admin storage policy
        var r = await sb.rpc("admin_avatar_decide", { p_email: email, p_action: act });
        if (!r || !r.data || !r.data.ok) throw new Error((r && r.data && r.data.error) || "failed");
      } catch (err) {
        alert("That didn’t go through: " + (err.message || err));
      }
      renderAvatars(panel, filter, view);          // stay on the same filter, search and page
      refreshAvatarCount();
    };
  }

  // Ask Shopify's CDN for a small thumbnail instead of the multi-MB original.
  function sizedImg(url, w) {
    if (!url || url.indexOf("cdn.shop") === -1) return url; // only Shopify CDN resizes
    return url + (url.indexOf("?") === -1 ? "?" : "&") + "width=" + w;
  }

  // Difficulty levels shown to admins — MUST match the play page dropdown.
  var DIFF_LEVELS = [[2, "Very Easy"], [3, "Easy"], [4, "Casual"], [5, "Medium"], [6, "Tricky"], [7, "Hard"], [8, "Expert"]];

  function renderCard(product, puzzle) {
    var shopifyImg = product.images && product.images.length
      ? sizedImg(product.images[0].src, 400)
      : null;
    var customImg = puzzle && puzzle.custom_image_url ? puzzle.custom_image_url : null;
    // The admin's uploaded image is what players see, so prefer it in the card too.
    var img = customImg || shopifyImg;
    var price = product.variants && product.variants.length ? product.variants[0].price : null;
    var needsImage = !img;

    // Difficulty = a numeric base 2..8 (default Easy=3); pieces are derived from it.
    var diffVal = puzzle && puzzle.difficulty ? parseInt(puzzle.difficulty, 10) : NaN;
    if (!(diffVal >= 2 && diffVal <= 8)) diffVal = 3;
    var pieceVal = puzzle && puzzle.piece_count != null ? puzzle.piece_count : diffVal * diffVal;
    var diffOptions = DIFF_LEVELS.map(function (d) {
      return '<option value="' + d[0] + '"' + (d[0] === diffVal ? " selected" : "") + ">" + d[1] + "</option>";
    }).join("");
    // Active/Inactive. A puzzle with NO row yet counts as ACTIVE (new puzzles default to
    // visible) - only an explicit is_active === false hides it. `active` is mutable: the
    // toggle updates it and baseRow() reads it, so Save/upload can never resurrect a
    // deactivated puzzle.
    var active = !(puzzle && puzzle.is_active === false);
    function toggleLabel() { return active ? "✓ Active — visible" : "✕ Inactive — hidden"; }

    var card = el(
      '<div class="wtp-card' +
        (needsImage ? " wtp-card--needs-image" : "") +
        (active ? "" : " wtp-card--inactive") +
        '">' +
        '<div class="wtp-card__media">' +
        (img
          ? '<img src="' + esc(img) + '" loading="lazy" alt="' + esc(product.title) + '">'
          : '<div class="wtp-card__badge">⚠ needs image</div>') +
        (customImg ? '<span class="wtp-card__tag">custom</span>' : "") +
        '<span class="wtp-card__hidden-tag">Hidden</span>' +
        "</div>" +
        '<div class="wtp-card__body">' +
        '<div class="wtp-card__title">' + esc(product.title) + "</div>" +
        '<div class="wtp-card__extras">' +
        '<select class="wtp-card__difficulty" title="Difficulty">' + diffOptions + "</select>" +
        '<input class="wtp-card__pieces" type="number" value="' + pieceVal +
        '" readonly title="Approx. pieces — the exact count adapts to the image shape">' +
        '<button class="wtp-card__save" type="button">Save</button>' +
        '<button class="wtp-card__active' + (active ? "" : " wtp-card__active--off") +
        '" type="button" title="Show or hide this puzzle on the play page">' + toggleLabel() + "</button>" +
        "</div>" +
        '<label class="wtp-card__upload">Upload image' +
        '<input type="file" accept="image/*"></label>' +
        '<div class="wtp-card__status"></div>' +
        "</div>" +
        "</div>",
    );

    var statusEl = card.querySelector(".wtp-card__status");
    function setStatus(msg, isErr) {
      statusEl.textContent = msg || "";
      statusEl.className = "wtp-card__status" + (isErr ? " wtp-card__status--err" : "");
    }

    // base row used by both save + upload upserts
    function baseRow() {
      return {
        shopify_product_id: String(product.id),
        handle: product.handle,
        title: product.title,
        image_url: shopifyImg,
        price: price != null ? Number(price) : null,
        is_active: active,
      };
    }

    // Pieces auto-adjust when the admin changes difficulty (base × base, nominal).
    var diffSel = card.querySelector(".wtp-card__difficulty");
    var piecesEl = card.querySelector(".wtp-card__pieces");
    diffSel.addEventListener("change", function () {
      var b = parseInt(diffSel.value, 10) || 3;
      piecesEl.value = b * b;
    });

    // Active/Inactive toggle - saves IMMEDIATELY (no separate Save press to forget).
    // Inactive puzzles vanish from /pages/play and can't be opened by direct URL; they stay
    // listed here so an admin can always find and re-activate them.
    var activeBtn = card.querySelector(".wtp-card__active");
    activeBtn.addEventListener("click", async function () {
      var next = !active;
      activeBtn.disabled = true;
      setStatus(next ? "Activating…" : "Deactivating…");
      var row = baseRow();
      row.is_active = next;
      var res = await sb.from("puzzles").upsert(row, { onConflict: "shopify_product_id" });
      activeBtn.disabled = false;
      if (res.error) return setStatus("Error: " + res.error.message, true);
      active = next;                       // baseRow() reads this from here on
      activeBtn.textContent = toggleLabel();
      activeBtn.classList.toggle("wtp-card__active--off", !active);
      card.classList.toggle("wtp-card--inactive", !active);
      setStatus(active ? "Active — visible to players ✓" : "Inactive — hidden from players ✓");
    });

    // Save extras: difficulty stored as the numeric base ("2".."8"); pieces derived.
    card.querySelector(".wtp-card__save").addEventListener("click", async function () {
      setStatus("Saving…");
      var row = baseRow();
      var base = parseInt(diffSel.value, 10) || 3;
      row.difficulty = String(base);
      row.piece_count = base * base;
      var res = await sb.from("puzzles").upsert(row, { onConflict: "shopify_product_id" });
      setStatus(res.error ? "Error: " + res.error.message : "Saved ✓", !!res.error);
    });

    // Upload image
    card.querySelector('input[type="file"]').addEventListener("change", async function (e) {
      var file = e.target.files && e.target.files[0];
      if (!file) return;
      setStatus("Uploading…");
      var ext = (file.name.split(".").pop() || "jpg").toLowerCase();
      var path = product.handle + "-" + product.id + "." + ext;
      var up = await sb.storage.from(BUCKET).upload(path, file, { upsert: true, contentType: file.type });
      if (up.error) return setStatus("Upload failed: " + up.error.message, true);
      var pub = sb.storage.from(BUCKET).getPublicUrl(path);
      // Cache-bust: the storage path is reused per product, so the public URL is
      // identical on every re-upload. Without a unique version stamp the browser
      // and Supabase CDN keep serving the OLD image. A ?v= timestamp forces a fresh load.
      var url = pub.data.publicUrl + "?v=" + Date.now();
      var row = baseRow();
      row.custom_image_url = url;
      var res = await sb.from("puzzles").upsert(row, { onConflict: "shopify_product_id" });
      if (res.error) return setStatus("Saved file but DB failed: " + res.error.message, true);
      // reflect in UI
      var media = card.querySelector(".wtp-card__media");
      media.innerHTML = '<img src="' + esc(url) + '" alt="' + esc(product.title) + '">' +
        '<span class="wtp-card__tag">custom</span><span class="wtp-card__hidden-tag">Hidden</span>';
      card.classList.remove("wtp-card--needs-image");
      setStatus("Image saved ✓");
    });

    return card;
  }

  // ---- boot -----------------------------------------------------------------
  whenSupabaseReady(async function (client) {
    sb = client;
    try {
      if (await isAdmin()) renderDashboard();
      else renderLogin();
    } catch (e) {
      renderLogin(e.message || String(e));
    }
  });
})();
